import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Loader2, PoundSterling, Send, Sparkles, Zap, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// "Quote a service plan" modal — opens from the diary / pre-calls row when
// EMaC has flagged this reg as `service_plan_opportunity: true` (i.e. no
// live plan on the vehicle). "Get live price" pulls real pricing from EMaC's
// Integration API v3; if that isn't switched on (or EMaC refuses), the
// advisor can still type a monthly price by hand.
export default function ServicePlanQuoteModal({ job, open, onClose, onSent }) {
  const [term, setTerm] = useState(24);
  const [annualMileage, setAnnualMileage] = useState(10000);
  const [motIncluded, setMotIncluded] = useState(false);
  const [monthly, setMonthly] = useState("");
  const [note, setNote] = useState("");
  const [channels, setChannels] = useState({ sms: true, email: true });
  const [sending, setSending] = useState(false);

  // --- Live EMaC pricing ---
  const [emacOn, setEmacOn] = useState(false);
  const [mileage, setMileage] = useState(job?.current_mileage || "");
  const [pulling, setPulling] = useState(false);
  const [live, setLive] = useState(null);      // normalised quote
  const [liveError, setLiveError] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/emac/status").then((r) => setEmacOn(!!r.data?.configured)).catch(() => setEmacOn(false));
  }, [open]);

  const liveQuote = live?.quotes?.[0] || null;
  const plans = liveQuote?.payment_plans || [];

  const getLivePrice = async () => {
    const mi = Number(mileage);
    if (!(mi > 0)) { toast.error("Enter the current mileage first."); return; }
    setPulling(true);
    setLiveError("");
    try {
      const r = await api.post(`/emac/jobs/${job.id}/quote`, {
        reg: job.vehicle_reg || "",
        current_mileage: mi,
        annual_mileage: annualMileage,
        term_months: term,
      });
      if (!r.data?.ok) {
        setLive(null);
        setLiveError(r.data?.error || "EMaC couldn't price this vehicle.");
        return;
      }
      setLive(r.data);
      const q = r.data.quotes?.[0];
      const pick = q?.payment_plans?.find((p) => p.id === q.selected_payment_plan_id) || q?.payment_plans?.[0];
      if (pick) {
        setSelectedPlanId(pick.id);
        setMonthly(String(pick.monthly || ""));
        if (pick.months) setTerm(pick.months);
      }
      toast.success("Live EMaC price loaded");
    } catch (e) {
      setLiveError(e?.response?.data?.detail || "Couldn't reach EMaC.");
    } finally {
      setPulling(false);
    }
  };

  const choosePlan = (p) => {
    setSelectedPlanId(p.id);
    setMonthly(String(p.monthly || ""));
    if (p.months) setTerm(p.months);
  };

  const total = (Number(monthly) || 0) * term;

  const send = async () => {
    const m = Number(monthly);
    if (!(m > 0)) { toast.error("Enter a monthly price."); return; }
    const chList = [];
    if (channels.sms && job.customer_phone) chList.push("sms");
    if (channels.email && job.customer_email) chList.push("email");
    if (!chList.length) { toast.error("No SMS or email address on file for this customer."); return; }
    setSending(true);
    try {
      await api.post(`/jobs/${job.id}/service-plan-quote`, {
        term_months: term,
        annual_mileage: annualMileage,
        mot_included: motIncluded,
        monthly_price: m,
        total_price: liveQuote?.total || total,
        channels: chList,
        custom_note: note,
        plan_name: liveQuote?.plan_name || undefined,
      });
      toast.success(`Quote sent to ${job.customer_name || "customer"}.`);
      onSent?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the quote — try again.");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const vehicle = [job.vehicle_year, job.vehicle_make, job.vehicle_model].filter(Boolean).join(" ");

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-6" data-testid="service-plan-quote-modal">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="border-b border-border px-5 py-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/15 text-warning grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-overline text-warning">Service plan opportunity</div>
            <div className="font-display font-bold text-lg leading-tight mt-0.5">
              Quote a plan for {job.customer_name?.split(" ")[0] || "the customer"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {vehicle}{job.vehicle_reg ? ` · ${job.vehicle_reg}` : ""} — no live plan found in EMaC.
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-muted rounded-md" data-testid="plan-quote-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Live EMaC pricing */}
          {emacOn && (
            <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-3 space-y-3" data-testid="emac-live-panel">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                <span className="label-overline text-primary">Live EMaC price</span>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <label className="label-overline block mb-1.5">Current mileage</label>
                  <input
                    type="number"
                    min="0"
                    value={mileage}
                    onChange={(e) => setMileage(e.target.value)}
                    placeholder="e.g. 42000"
                    className="input-default text-sm w-full"
                    data-testid="emac-mileage-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={getLivePrice}
                  disabled={pulling}
                  className="btn-primary text-sm inline-flex items-center gap-2 whitespace-nowrap"
                  data-testid="emac-get-live-price"
                >
                  {pulling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                  Get live price
                </button>
              </div>

              {liveError && (
                <div className="flex items-start gap-2 text-xs text-warning" data-testid="emac-live-error">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{liveError} You can still enter a monthly price by hand below.</span>
                </div>
              )}

              {live?.vehicle?.make && (
                <div className="text-xs text-muted-foreground" data-testid="emac-live-vehicle">
                  EMaC matched: <span className="font-semibold text-foreground">
                    {[live.vehicle.make, live.vehicle.model, live.vehicle.variant].filter(Boolean).join(" ")}
                  </span>
                </div>
              )}

              {plans.length > 0 && (
                <div className="space-y-1.5" data-testid="emac-plan-list">
                  {plans.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => choosePlan(p)}
                      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                        selectedPlanId === p.id
                          ? "border-primary bg-primary/10"
                          : "border-border bg-background/40 hover:bg-muted"
                      }`}
                      data-testid={`emac-plan-${p.id}`}
                    >
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-bold">{p.name}</span>
                        <span className="font-mono-plex text-sm font-bold">
                          {p.monthly > 0 ? `£${p.monthly.toFixed(2)}/mo` : `£${p.total.toFixed(2)}`}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {p.months ? `${p.months} payments · ` : ""}
                        {p.deposit > 0 ? `£${p.deposit.toFixed(2)} deposit · ` : ""}
                        Total £{p.total.toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {liveQuote?.services?.length > 0 && (
                <details className="text-xs" data-testid="emac-services-details">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-semibold">
                    What's covered ({liveQuote.services.length} services)
                  </summary>
                  <ul className="mt-1.5 space-y-1">
                    {liveQuote.services.map((s, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span>
                          {s.name}
                          {s.due_mileage ? ` · ${s.due_mileage.toLocaleString()} mi` : ""}
                        </span>
                        <span className="font-mono-plex">£{(s.price_to_pay || s.price).toFixed(2)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div>
            <label className="label-overline block mb-1.5">Term</label>
            <div className="flex gap-1.5">
              {[24, 36, 48].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTerm(t)}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${
                    term === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background/40 text-muted-foreground border-border hover:bg-muted"
                  }`}
                  data-testid={`plan-term-${t}`}
                >
                  {t} months
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-overline block mb-1.5">Annual mileage</label>
            <div className="flex gap-1.5 flex-wrap">
              {[6000, 8000, 10000, 12000, 15000, 20000].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setAnnualMileage(m)}
                  className={`px-3 py-1.5 rounded-md text-xs font-bold border transition-colors ${
                    annualMileage === m
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background/40 text-muted-foreground border-border hover:bg-muted"
                  }`}
                  data-testid={`plan-mileage-${m}`}
                >
                  {m.toLocaleString()} mi
                </button>
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={motIncluded}
              onChange={(e) => setMotIncluded(e.target.checked)}
              className="rounded border-border accent-warning"
              data-testid="plan-mot-included"
            />
            <span>MOT included</span>
          </label>

          <div>
            <label className="label-overline block mb-1.5">Monthly price</label>
            <div className="relative">
              <PoundSterling className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="number"
                min="0"
                step="0.01"
                value={monthly}
                onChange={(e) => setMonthly(e.target.value)}
                placeholder="e.g. 24.99"
                className="input-default text-sm pl-9 w-full"
                data-testid="plan-monthly-input"
                autoFocus
              />
            </div>
            {total > 0 && (
              <div className="text-xs text-muted-foreground mt-1.5" data-testid="plan-total-preview">
                Total across {term} months: <span className="font-mono-plex font-bold text-foreground">£{total.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div>
            <label className="label-overline block mb-1.5">Custom note (optional)</label>
            <textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Locks in this year's servicing at today's price"
              className="input-default text-sm w-full resize-none"
              data-testid="plan-note-input"
            />
          </div>

          <div className="border-t border-border pt-3 space-y-2">
            <div className="label-overline">Send via</div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channels.sms && !!job.customer_phone}
                disabled={!job.customer_phone}
                onChange={(e) => setChannels({ ...channels, sms: e.target.checked })}
                className="rounded border-border"
                data-testid="plan-channel-sms"
              />
              <span>SMS {!job.customer_phone && <em className="text-muted-foreground">(no phone number)</em>}</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={channels.email && !!job.customer_email}
                disabled={!job.customer_email}
                onChange={(e) => setChannels({ ...channels, email: e.target.checked })}
                className="rounded border-border"
                data-testid="plan-channel-email"
              />
              <span>Email {!job.customer_email && <em className="text-muted-foreground">(no email on file)</em>}</span>
            </label>
          </div>
        </div>

        <div className="border-t border-border px-5 py-3 flex items-center gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-outline text-sm" data-testid="plan-quote-cancel">Cancel</button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !Number(monthly)}
            className="btn-primary text-sm inline-flex items-center gap-2"
            data-testid="plan-quote-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send quote
          </button>
        </div>
      </div>
    </div>
  );
}
